import React, {useEffect, useState} from 'react';

import {Link} from 'react-router-dom';

import {StopFilled, StopOutlined} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Skeleton,
  Space,
  Tooltip,
  Typography
} from 'antd';
import moment from 'moment';

import type {BlacklistItem, SearchableChargeFilterInput} from 'api/generated';
import {
  BlacklistItemType,
  useAddBlacklistItemMutation,
  useBlacklistItemQuery,
  useChargesLazyQuery,
  useDeleteBlacklistItemMutation
} from 'api/generated';
import {blacklistItemsQuery} from 'api/queries';
import {Hint} from 'components/Hint';
import {normalizeBlacklistValidUntil} from 'routes/Account/Blacklist/blacklistValidUntil';
import {colors} from 'lib/colors';
import {formatNumber, getBlacklistPaymentsUrl, getLast3MonthsUnixRange} from 'lib/formatters';
import {blacklistItemTypeLabels} from 'lib/labels';

const {Text} = Typography;

const GLOBAL_ACCOUNT_ID = 'global';

const BLACKLIST_TYPE_FILTER: Partial<
  Record<BlacklistItemType, (v: string) => SearchableChargeFilterInput>
> = {
  [BlacklistItemType.EMAIL]: (v) => ({customerEmail: {match: v}}),
  [BlacklistItemType.PHONE]: (v) => ({customerPhone: {match: v}}),
  [BlacklistItemType.IP]: (v) => ({sessionIp: {match: v}}),
  [BlacklistItemType.CARD_FINGERPRINT]: (v) => ({paymentMethodCardFingerprint: {match: v}}),
  [BlacklistItemType.SESSION_FINGERPRINT]: (v) => ({sessionFingerprint: {match: v}})
};

const BLACKLIST_TYPE_FILTER_KEY: Partial<Record<BlacklistItemType, string>> = {
  [BlacklistItemType.EMAIL]: 'customerEmail',
  [BlacklistItemType.PHONE]: 'customerPhone',
  [BlacklistItemType.IP]: 'sessionIp',
  [BlacklistItemType.CARD_FINGERPRINT]: 'paymentMethodCardFingerprint',
  [BlacklistItemType.SESSION_FINGERPRINT]: 'sessionFingerprint'
};

const FINGERPRINT_TYPES = new Set([
  BlacklistItemType.CARD_FINGERPRINT,
  BlacklistItemType.SESSION_FINGERPRINT
]);

const EXPIRATION_PRESETS = [
  {label: 'Tomorrow', value: () => moment().add(1, 'day')},
  {label: '7 days', value: () => moment().add(7, 'days')},
  {label: '14 days', value: () => moment().add(14, 'days')},
  {label: '1 month', value: () => moment().add(1, 'month')}
] as const;

type Props = {
  type: BlacklistItemType;
  value?: string | null;
  formattedValue?: React.ReactNode;
  copyable?: string | null;
  note?: string | null;
  accountId?: string;
};

export const BlacklistValue: React.FC<Props> = ({
  type,
  value,
  formattedValue,
  copyable,
  note,
  accountId
}) => {
  const rawValue = value ?? undefined;
  const [isAddOpen, setAddOpen] = useState(false);
  const [isRemoveOpen, setRemoveOpen] = useState(false);
  const [inputNote, setInputNote] = useState('');
  const [validUntil, setValidUntil] = useState<moment.Moment | null>(null);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [isCountLoading, setCountLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [scope, setScope] = useState<string>(accountId ?? 'global');
  const [removeScope, setRemoveScope] = useState<'account' | 'global' | 'both'>('both');

  const hasAccountScope = !!accountId && accountId !== GLOBAL_ACCOUNT_ID;

  const {
    data: accountData,
    loading: accountLoading,
    refetch: refetchAccount
  } = useBlacklistItemQuery({
    variables: {value: rawValue ?? '', accountId},
    skip: !rawValue || !hasAccountScope,
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network',
    context: {useApolloNetworkStatus: false}
  });

  const {
    data: globalData,
    loading: globalLoading,
    refetch: refetchGlobal
  } = useBlacklistItemQuery({
    variables: {value: rawValue ?? '', accountId: GLOBAL_ACCOUNT_ID},
    skip: !rawValue,
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network',
    context: {useApolloNetworkStatus: false}
  });

  const [addBlacklistItem] = useAddBlacklistItemMutation();
  const [deleteBlacklistItem] = useDeleteBlacklistItemMutation();
  const [fetchCharges] = useChargesLazyQuery();

  const isAccountBlacklisted = hasAccountScope && accountData?.blacklistItem?.type === type;
  const isGlobalBlacklisted = globalData?.blacklistItem?.type === type;
  const isBlacklisted = isAccountBlacklisted || isGlobalBlacklisted;
  const loading = accountLoading || globalLoading;

  const refetchBoth = async () => {
    await Promise.all([
      hasAccountScope ? refetchAccount().catch(() => undefined) : undefined,
      refetchGlobal().catch(() => undefined)
    ]);
  };

  useEffect(() => {
    if (!isAddOpen || !rawValue) return;
    if (scope === 'global') setInputNote('');
    const filterFn = BLACKLIST_TYPE_FILTER[type];
    const filterKey = BLACKLIST_TYPE_FILTER_KEY[type];
    if (!filterFn || !filterKey) return;
    setMatchCount(null);
    setCountLoading(true);
    fetchCharges({
      variables: {
        filter: {
          ...filterFn(rawValue),
          createdAt: {range: getLast3MonthsUnixRange()},
          ...(scope !== 'global' ? {accountId: {match: scope}} : {})
        },
        size: 1
      }
    })
      .then(({data: chargesData}) => {
        setMatchCount(chargesData?.charges.total ?? 0);
      })
      .catch(() => {
        setMatchCount(0);
      })
      .finally(() => {
        setCountLoading(false);
      });
  }, [isAddOpen, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFingerprint = FINGERPRINT_TYPES.has(type);

  const handleBlacklist = async (targetAccountId?: string) => {
    if (!rawValue) return;
    setSubmitting(true);
    try {
      await addBlacklistItem({
        variables: {
          input: {
            type,
            value: rawValue,
            note:
              isFingerprint && note
                ? [note, inputNote].filter(Boolean).join(' — ')
                : inputNote || undefined,
            accountId: targetAccountId || GLOBAL_ACCOUNT_ID,
            validUntil: normalizeBlacklistValidUntil(validUntil)
          }
        },
        refetchQueries: [
          {query: blacklistItemsQuery, variables: {accountId: GLOBAL_ACCOUNT_ID}},
          ...(targetAccountId
            ? [{query: blacklistItemsQuery, variables: {accountId: targetAccountId}}]
            : [])
        ]
      });
      await refetchBoth();
      setAddOpen(false);
      setInputNote('');
      setValidUntil(null);
      message.success('Added to blacklist');
    } catch {
      message.error('Could not add to blacklist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveBlacklist = async () => {
    if (!rawValue) return;
    setSubmitting(true);
    try {
      const deletions: Promise<unknown>[] = [];
      if (
        (removeScope === 'account' || removeScope === 'both') &&
        isAccountBlacklisted &&
        accountId
      ) {
        deletions.push(
          deleteBlacklistItem({
            variables: {accountId, value: rawValue},
            refetchQueries: [{query: blacklistItemsQuery, variables: {accountId}}]
          })
        );
      }
      if ((removeScope === 'global' || removeScope === 'both') && isGlobalBlacklisted) {
        deletions.push(
          deleteBlacklistItem({
            variables: {accountId: GLOBAL_ACCOUNT_ID, value: rawValue},
            refetchQueries: [
              {query: blacklistItemsQuery, variables: {accountId: GLOBAL_ACCOUNT_ID}}
            ]
          })
        );
      }
      await Promise.all(deletions);
      await refetchBoth();
      setRemoveOpen(false);
      message.success('Removed from blacklist');
    } catch {
      message.error('Could not remove from blacklist');
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = blacklistItemTypeLabels[type];
  const displayLabel = isFingerprint && note ? note : rawValue;
  const existingNote =
    (accountData?.blacklistItem as BlacklistItem | undefined)?.note ??
    (globalData?.blacklistItem as BlacklistItem | undefined)?.note;
  const filterKey = BLACKLIST_TYPE_FILTER_KEY[type];
  const paymentsUrl =
    filterKey && rawValue
      ? getBlacklistPaymentsUrl(filterKey, rawValue, scope !== 'global' ? scope : undefined)
      : null;

  const displayValue = formattedValue ?? rawValue ?? null;
  const renderExpirationFooter = () => (
    <Space size={4} wrap>
      {EXPIRATION_PRESETS.map((preset) => (
        <Button
          key={preset.label}
          size="small"
          type="link"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            setValidUntil(preset.value());
          }}>
          {preset.label}
        </Button>
      ))}
      <Button
        size="small"
        type="link"
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          setValidUntil(null);
        }}>
        Clear
      </Button>
    </Space>
  );

  if (!rawValue) {
    return null;
  }

  return (
    <>
      <Space size="small">
        <span>
          {displayValue}
          {copyable != null && copyable !== '' && <Text copyable={{text: copyable}} />}
        </span>
        {isBlacklisted ? (
          <Tooltip
            title={[
              `Blacklisted (${
                isAccountBlacklisted && isGlobalBlacklisted
                  ? 'account + global'
                  : isAccountBlacklisted
                    ? 'account'
                    : 'global'
              })`,
              existingNote
            ]
              .filter(Boolean)
              .join(': ')}>
            <Button
              type="text"
              shape="circle"
              size="small"
              danger
              icon={<StopFilled style={{color: colors.red}} />}
              disabled={loading}
              onClick={() => {
                setRemoveScope(
                  isAccountBlacklisted && isGlobalBlacklisted
                    ? 'both'
                    : isAccountBlacklisted
                      ? 'account'
                      : 'global'
                );
                setRemoveOpen(true);
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Add to blacklist">
            <Button
              type="text"
              shape="circle"
              size="small"
              icon={<StopOutlined style={{color: colors.secondary}} />}
              disabled={loading}
              onClick={() => {
                setScope(accountId ?? 'global');
                setAddOpen(true);
              }}
            />
          </Tooltip>
        )}
      </Space>

      <Modal
        centered
        open={isAddOpen}
        title={`Add ${typeLabel} to blacklist`}
        okText="Add to blacklist"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={isSubmitting}
        onOk={() => {
          void handleBlacklist(scope === 'global' ? undefined : scope);
        }}
        onCancel={() => {
          setAddOpen(false);
          setInputNote('');
          setValidUntil(null);
        }}>
        <span>
          All future payments matching this {typeLabel}{' '}
          <strong style={{whiteSpace: 'nowrap'}}>{displayLabel}</strong> will be automatically
          blocked. Are you sure?
        </span>
        {filterKey && (
          <div style={{marginTop: 8}}>
            {isCountLoading ? (
              <Skeleton.Button active size="small" style={{width: 280, height: 20}} />
            ) : matchCount !== null && matchCount > 0 && paymentsUrl ? (
              <Text>
                This would have blocked{' '}
                <Link to={paymentsUrl} style={{fontWeight: 'bold'}}>
                  {formatNumber(matchCount)} payments
                </Link>{' '}
                in the last 3 months.
              </Text>
            ) : (
              <Text type="secondary">No matching payments found in the last 3 months.</Text>
            )}
          </div>
        )}
        {accountId && (
          <Radio.Group
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as string);
            }}
            style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
            <Radio value={accountId}>Account blacklist</Radio>
            <Radio value="global">Global blacklist</Radio>
          </Radio.Group>
        )}
        <Form layout="vertical" style={{marginTop: 12}}>
          <Form.Item
            label={
              <Hint
                title="Expires"
                message="Blacklist items are automatically removed after this date. Leave empty to create a permanent rule."
              />
            }
            style={{marginBottom: 0, marginTop: 12}}>
            <DatePicker
              value={validUntil}
              format="ll"
              style={{width: '100%'}}
              showToday={false}
              renderExtraFooter={renderExpirationFooter}
              onChange={(date) => {
                setValidUntil(date);
              }}
              disabledDate={(date) => date.isBefore(moment().startOf('day'))}
            />
          </Form.Item>
          <Form.Item label="Optional note or reason" style={{marginBottom: 0, marginTop: 12}}>
            <Input
              value={inputNote}
              onChange={(e) => {
                setInputNote(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        centered
        open={isRemoveOpen}
        title="Remove from blacklist"
        okText="Remove from blacklist"
        cancelText="Cancel"
        confirmLoading={isSubmitting}
        onOk={() => {
          void handleRemoveBlacklist();
        }}
        onCancel={() => {
          setRemoveOpen(false);
        }}>
        <span>
          Payments matching this {typeLabel} <strong>{displayLabel}</strong> will no longer be
          blocked. Are you sure?
        </span>
        {isAccountBlacklisted && isGlobalBlacklisted && (
          <Radio.Group
            value={removeScope}
            onChange={(e) => {
              setRemoveScope(e.target.value as 'account' | 'global' | 'both');
            }}
            style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
            <Radio value="account">Remove from account blacklist</Radio>
            <Radio value="global">Remove from global blacklist</Radio>
            <Radio value="both">Remove from both</Radio>
          </Radio.Group>
        )}
        {existingNote && (
          <Text type="secondary" style={{display: 'block', marginTop: 8}}>
            {existingNote}
          </Text>
        )}
      </Modal>
    </>
  );
};
